/**
 * 詳細画面のタブ。選択状態は URL の `?tab=` に置く。
 *
 * コンポーネント state に持たないのは、運用中に「この画面のこのタブを見て」と
 * URL を共有する場面が多いため（ノード詳細の metrics タブなど）。
 */
import * as React from 'react';
import { cn } from '@mail-sv/ui';

export interface PageTab<T extends string> {
  value: T;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** 権限が無いタブは描画しない。 */
  visible?: boolean;
}

export function PageTabs<T extends string>({
  tabs,
  value,
  onChange,
  className,
}: {
  tabs: ReadonlyArray<PageTab<T>>;
  value: T;
  onChange: (next: T) => void;
  className?: string;
}): React.ReactElement {
  const visible = tabs.filter((t) => t.visible !== false);
  return (
    <div className={cn('mb-5 overflow-x-auto border-b border-border', className)}>
      <div className="flex min-w-max gap-1" role="tablist">
        {visible.map((tab) => {
          const active = tab.value === value;
          const Icon = tab.icon;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(tab.value)}
              className={cn(
                'relative flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {Icon ? <Icon className="size-4" /> : null}
              {tab.label}
              {active ? (
                <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" aria-hidden />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
